import React, { useEffect, useState } from 'react';
import s from './NovaTrending.module.sass';
import { useAppContext } from '../../app-core/AppContext';
import { Flame } from 'lucide-react';
import useFitGrid from './useFitGrid';

// Высота строки тренда (контент + разделитель) — база для расчёта плотности
const ROW_H = 39;

/**
 * NovaTrending — блок «В тренде» (CoinGecko /search/trending).
 * Нижний ряд «кирпичной кладки» дашборда: узкий блок слева, под балансом.
 * Видимостью управляет родитель (NovaDashboard), не сам компонент.
 */
const API_URL = 'https://api.coingecko.com/api/v3/search/trending';
const REFRESH_MS = 5 * 60 * 1000;
// Тянем весь пул CoinGecko (до 15) — сколько показать, решает уже вёрстка
const MAX_ITEMS = 15;

// Кэш на уровне модуля — переживает перемонтирования при смене разделов
let cache = { items: null, fetchedAt: 0 };

export default function NovaTrending() {
    const { safeT } = useAppContext();
    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;

    const [items, setItems] = useState(cache.items);
    // Плотность постоянна: показываем ровно столько строк, сколько влезает
    const { ref: listRef, rows } = useFitGrid({ rowHeight: ROW_H, minRows: 6 });

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const res = await fetch(API_URL);
                if (!res.ok) return;
                const json = await res.json();
                const list = (json.coins || []).slice(0, MAX_ITEMS).map(({ item }) => ({
                    id: item.id,
                    name: item.name,
                    symbol: (item.symbol || '').toUpperCase(),
                    icon: item.small || item.thumb,
                    rank: item.market_cap_rank,
                    change24h: item.data?.price_change_percentage_24h?.usd ?? null,
                }));
                if (!list.length) return;
                cache = { items: list, fetchedAt: Date.now() };
                if (!cancelled) setItems(list);
            } catch (_) { /* сеть недоступна — остаёмся на кэше */ }
        };

        if (!cache.items || Date.now() - cache.fetchedAt > REFRESH_MS) load();
        const timer = setInterval(load, REFRESH_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, []);

    if (!items || !items.length) return null;

    return (
        <div className={s.wrap}>
            <span className={s.tab}>
                <Flame size={12} strokeWidth={2} aria-hidden="true" />
                {t('dashboard.trendingTitle', 'В тренде')}
            </span>
            <div className={s.card}>
                <ul className={s.list} ref={listRef}>
                    {items.slice(0, Math.min(rows, items.length)).map((coin) => {
                        const positive = coin.change24h === null ? null : coin.change24h >= 0;
                        return (
                            <li key={coin.id} className={s.item}>
                                <img src={coin.icon} alt="" className={s.icon} loading="lazy" />
                                <span className={s.names}>
                                    <span className={s.name}>{coin.name}</span>
                                    <span className={s.symbol}>{coin.symbol}</span>
                                </span>
                                {coin.rank != null && (
                                    <span className={s.rankBadge} title={t('dashboard.trendingRank', 'Ранг по капитализации')}>
                                        #{coin.rank}
                                    </span>
                                )}
                                {positive !== null && (
                                    <span className={`${s.changeBadge} ${positive ? s.up : s.down}`}>
                                        {positive ? '↑' : '↓'} {Math.abs(coin.change24h).toFixed(1)}%
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
